/**
 * Extracts the YouTube video ID from a given URL.
 * Supports various YouTube URL formats including standard watch URLs, 
 * shortened URLs, embed URLs, shorts, live streams, and channel videos.
 *
 * @param {string} url - The YouTube URL to extract the video ID from.
 * @returns {string|null} The 11-character video ID, or null if not found.
 * @throws {TypeError} If the input is not a string.
 */
export const detectYouTubeVideoId = (url) => {
    // Validate input
    if (typeof url !== 'string') {
        throw new TypeError('URL must be a string');
    }

    // Handle empty inputs
    if (!url.trim()) {
        return null;
    }

    // The standard YouTube video ID regex pattern
    const VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

    // Common YouTube URL patterns - ordered from most to least common
    const URL_PATTERNS = [
        // Standard watch URL with v parameter
        /(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:&|\?|\/|$)/,
        // Embed URLs
        /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})(?:\?|\/|$)/,
        // Shorts URLs
        /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})(?:\?|\/|$)/,
        // Live stream URLs
        /youtube\.com\/live\/([a-zA-Z0-9_-]{11})(?:\?|\/|$)/,
        // Legacy URLs
        /youtube\.com\/v\/([a-zA-Z0-9_-]{11})(?:\?|\/|$)/,
        /youtube\.com\/vi\/([a-zA-Z0-9_-]{11})(?:\?|\/|$)/,
        // User URLs
        /youtube\.com\/user\/.*#p\/.*\/([a-zA-Z0-9_-]{11})(?:\?|\/|$)/
    ];

    // Try to match using regex patterns first (most efficient)
    for (const pattern of URL_PATTERNS) {
        const match = url.match(pattern);
        if (match && match[1] && VIDEO_ID_PATTERN.test(match[1])) {
            return match[1];
        }
    }

    // Fall back to URL parsing for more complex cases
    try {
        const parsedUrl = new URL(url);

        // Check for 'v' query parameter (most common)
        const vParam = parsedUrl.searchParams.get('v');
        if (vParam && VIDEO_ID_PATTERN.test(vParam)) {
            return vParam;
        }

        // Check path segments for potential video IDs
        const pathSegments = parsedUrl.pathname
            .split('/')
            .filter(segment => segment.length > 0);

        for (const segment of pathSegments) {
            if (VIDEO_ID_PATTERN.test(segment)) {
                return segment;
            }
        }
    } catch (error) {
        // Invalid URLs or parsing errors are silently ignored
        // as per the function's contract to return null for invalid inputs
    }

    return null;
};

/**
 * Generates a YouTube embed URL for a given video ID.
 * 
 * @param {string} videoId - The YouTube video ID (11-character string)
 * @param {Object} [options] - Optional parameters for the embed URL
 * @param {boolean} [options.autoplay=false] - Whether to autoplay the video
 * @param {boolean} [options.controls=true] - Whether to show video controls
 * @returns {string} The YouTube embed URL
 * @throws {TypeError} If the video ID is not a valid string
 */
export const getYouTubeEmbedUrl = (videoId, options = {}) => {
    // Validate video ID
    if (typeof videoId !== 'string' || !videoId.trim()) {
        throw new TypeError('Video ID must be a non-empty string');
    }

    // Check if it matches the YouTube ID pattern
    const VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;
    if (!VIDEO_ID_PATTERN.test(videoId)) {
        throw new TypeError('Invalid YouTube video ID format');
    }

    // Build the base URL
    const baseUrl = `https://www.youtube.com/embed/${videoId}`;

    // Add optional parameters
    const params = new URLSearchParams();
    if (options.autoplay) params.append('autoplay', '1');
    if (options.controls === false) params.append('controls', '0');

    // Return the complete URL
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};